import type { DuplicateStrategy, PatientNameFormat } from "@bookdental/schemas";

export function ComposeAddressPanel({
  columns,
  selected,
  onChange,
}: {
  columns: string[];
  selected: string[];
  onChange: (v: string[]) => void;
}) {
  return (
    <div className="rounded-lg border border-zinc-200 bg-zinc-50/50 p-3">
      <p className="text-xs font-semibold text-zinc-900">Compor endereco (opcional)</p>
      <p className="mt-0.5 text-[11px] text-zinc-500">
        Selecione colunas pra concatenar num endereco completo.
      </p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {columns.map((c) => {
          const on = selected.includes(c);
          return (
            <button
              key={c}
              type="button"
              onClick={() =>
                onChange(on ? selected.filter((x) => x !== c) : [...selected, c])
              }
              className={`rounded-md border px-2 py-1 text-[11px] transition-colors ${
                on
                  ? "border-brand-400 bg-brand-100 text-brand-700"
                  : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300"
              }`}
            >
              {c}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function NameFormatPanel({
  value,
  onChange,
}: {
  value: PatientNameFormat;
  onChange: (v: PatientNameFormat) => void;
}) {
  const opts: { key: PatientNameFormat; label: string; sample: string }[] = [
    { key: "titlecase", label: "Primeira maiuscula", sample: "Maria da Silva" },
    { key: "uppercase", label: "Tudo maiusculo", sample: "MARIA DA SILVA" },
    { key: "original", label: "Manter original", sample: "como esta no arquivo" },
  ];
  return (
    <div className="rounded-lg border border-zinc-200 bg-zinc-50/50 p-3">
      <p className="text-xs font-semibold text-zinc-900">Formatacao de nomes</p>
      <div className="mt-2 grid gap-2 sm:grid-cols-3">
        {opts.map((o) => {
          const on = value === o.key;
          return (
            <button
              key={o.key}
              type="button"
              onClick={() => onChange(o.key)}
              className={`rounded-md border px-3 py-2 text-left transition-colors ${
                on
                  ? "border-brand-400 bg-white ring-2 ring-brand-200"
                  : "border-zinc-200 bg-white hover:border-zinc-300"
              }`}
            >
              <p className="text-xs font-semibold text-zinc-900">{o.label}</p>
              <p className="mt-0.5 text-[10px] text-zinc-500">{o.sample}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function DuplicatePanel({
  value,
  onChange,
}: {
  value: DuplicateStrategy;
  onChange: (v: DuplicateStrategy) => void;
}) {
  const opts: { key: DuplicateStrategy; label: string; desc: string }[] = [
    { key: "skip", label: "Pular duplicatas", desc: "Mantem o existente, ignora o novo" },
    { key: "update", label: "Atualizar existentes", desc: "Sobrescreve com os novos dados" },
    { key: "create_always", label: "Criar sempre", desc: "Permite duplicados no banco" },
  ];
  return (
    <div className="rounded-lg border border-zinc-200 bg-zinc-50/50 p-3">
      <p className="text-xs font-semibold text-zinc-900">Estrategia para duplicatas</p>
      <p className="mt-0.5 text-[11px] text-zinc-500">
        Detectadas por CPF ou por nome + data de nascimento.
      </p>
      <div className="mt-2 grid gap-2 sm:grid-cols-3">
        {opts.map((o) => {
          const on = value === o.key;
          return (
            <button
              key={o.key}
              type="button"
              onClick={() => onChange(o.key)}
              className={`rounded-md border px-3 py-2 text-left transition-colors ${
                on
                  ? "border-brand-400 bg-white ring-2 ring-brand-200"
                  : "border-zinc-200 bg-white hover:border-zinc-300"
              }`}
            >
              <p className="text-xs font-semibold text-zinc-900">{o.label}</p>
              <p className="mt-0.5 text-[10px] text-zinc-500">{o.desc}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function PreImportSummary({
  total,
  mapped,
  addressCount,
  nameFormat,
  duplicateStrategy,
}: {
  total: number;
  mapped: number;
  addressCount: number;
  nameFormat: PatientNameFormat;
  duplicateStrategy: DuplicateStrategy;
}) {
  const nf =
    nameFormat === "titlecase"
      ? "Primeira maiuscula"
      : nameFormat === "uppercase"
      ? "Tudo maiusculo"
      : "Original";
  const ds =
    duplicateStrategy === "skip"
      ? "Pular duplicatas"
      : duplicateStrategy === "update"
      ? "Atualizar existentes"
      : "Criar sempre";
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4">
      <p className="mb-2 text-xs font-semibold text-zinc-900">Pronto pra importar</p>
      <dl className="space-y-1.5 text-xs text-zinc-700">
        <Row label="Registros a processar" value={String(total)} />
        <Row label="Colunas mapeadas" value={String(mapped)} />
        <Row label="Formatacao de nomes" value={nf} />
        <Row label="Estrategia de duplicatas" value={ds} />
        {addressCount > 0 && (
          <Row label="Endereco composto" value={`${addressCount} coluna(s)`} />
        )}
      </dl>
      <p className="mt-3 text-[10px] text-zinc-500">
        CPF, telefone e data sao formatados automaticamente.
      </p>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-zinc-500">{label}</dt>
      <dd className="font-medium text-zinc-900">{value}</dd>
    </div>
  );
}
